// Pure helpers for the patient visit summary's AI-suggested warning signs. The renderer
// (access-emr-core PatientSummaries::Renderer) marks AI-suggested warning-sign list items with an
// inline `background-color: #fff4cc;` span — react-quill (and any HTML sanitizer) strips unknown
// attributes/classes but keeps inline color/background-color styles, so this is the only reliable
// signal. Mirrors the scribe highlight approach in ./scribeNote.ts.

export const SUGGESTED_BG = '#fff4cc'
// Browsers (and jsdom) normalise any valid CSS color — hex with/without spaces, upper/lower case,
// rgb() with/without spaces — to this canonical rgb() form via CSSStyleDeclaration, so comparing
// against just these two strings covers every form the backend note describes.
const SUGGESTED_BG_RGB = 'rgb(255,244,204)'

// Parse into a document created purely for markup manipulation — never the live document — so
// nothing in the (already server-sanitised) HTML can execute.
const parse = (html: string): HTMLElement => {
  const root = new DOMParser().parseFromString('<body></body>', 'text/html').body
  root.innerHTML = html
  return root
}

const isSuggestedBg = (el: HTMLElement): boolean => {
  const bg = el.style.backgroundColor.replace(/\s/g, '').toLowerCase()
  return bg === SUGGESTED_BG || bg === SUGGESTED_BG_RGB
}

// Does this (instructions or translation) HTML contain any AI-suggested warning sign? Drives the
// "Yellow items were suggested by AI — please check" banner.
export const hasSuggestionHighlight = (html: string | null | undefined): boolean => {
  if (!html) return false
  const root = parse(html)
  return Array.from(root.querySelectorAll<HTMLElement>('span[style]')).some(isSuggestedBg)
}

// Removes the AI-suggestion background (keeping the text, and keeping any other inline style —
// notably the red #b42318 warning heading colour, which lives on a different span and is never
// touched here) — used by the patient-facing document (VisitSummaryDocument), which must never
// show the suggestion highlight. Completion itself is stripped server-side
// (access-emr-core PatientSummaries::SuggestionStripper), so the stored/snapshotted HTML is clean
// and no re-translation is triggered.
export const stripSuggestionHighlight = (html: string | null | undefined): string => {
  if (!html) return html ?? ''
  const root = parse(html)
  root.querySelectorAll<HTMLElement>('span[style]').forEach(span => {
    if (!isSuggestedBg(span)) return
    span.style.backgroundColor = ''
    if (span.style.length === 0) {
      span.removeAttribute('style')
      span.replaceWith(...Array.from(span.childNodes))
    }
  })
  return root.innerHTML
}
